import {
  ColDef,
  TooltipCallbackParams,
  CellClassParams,
  ValueGetterParams
} from 'ag-grid-community';

import { BillingReportRow } from '../types';

import { defaultColWithErrorsFormatter } from './defaultColWithErrorsFormatter';
import { isoDateOnlyComparator, isoDateTimeComparator } from './isoDateComparator';
import { isoDateFormatter, isoDateTimeFormatter } from './isoDateTimeFormatters';
import { titleize } from './titleize';

/**
 * A column of member PHI. Adds `mp-sensitive` alongside the error highlighting, so the cell stays
 * out of Mixpanel and blurs with "Hide PHI".
 */
export function sensitiveColWithErrorsColDef(
  field: keyof BillingReportRow,
  options: Partial<ColDef> = {}
): ColDef {
  const colDef = colWithErrorsColDef(field, options);
  const { cellClass } = colDef;

  return {
    ...colDef,
    cellClass: (params: CellClassParams<BillingReportRow>) => {
      const base = typeof cellClass === 'function' ? cellClass(params) : cellClass;
      return [...(Array.isArray(base) ? base : base ? [base] : []), 'mp-sensitive'];
    }
  };
}

export function nullifyRowValue(field: keyof BillingReportRow) {
  return (params: ValueGetterParams<BillingReportRow>) => {
    const value = params.data?.[field];
    return value === '' ? null : value;
  };
}

export function colWithErrorsColDef(
  field: keyof BillingReportRow,
  options: Partial<ColDef> = {}
): ColDef {
  return {
    ...{
      field,
      headerName: titleize(field),
      resizable: true,
      sortable: true,
      minWidth: 0,
      filter: 'agTextColumnFilter',
      valueFormatter: defaultColWithErrorsFormatter,
      tooltip: (params: TooltipCallbackParams<BillingReportRow, string>) => {
        if (!params.colDef || !('field' in params.colDef)) return '';

        const col = params.colDef.field as keyof BillingReportRow;

        if (params.data && params.data.errors[col]) {
          return params.data.errors[col].join(',');
        } else if (params.data && params.data.warnings[col]) {
          return params.data.warnings[col].join(',');
        }

        return '';
      },
      cellClass: (params: CellClassParams<BillingReportRow>) => {
        if (!params.colDef || !('field' in params.colDef)) return '';

        const col = params.colDef.field as keyof BillingReportRow;

        if (params.data && params.data.errors[col]) {
          return ['bg-red-200'];
        } else if (params.data && params.data.warnings[col]) {
          return ['bg-yellow-200'];
        }
        return null;
      }
    },
    ...options
  };
}
export function dateColWithErrorsColDef(
  field: keyof BillingReportRow,
  options: Partial<ColDef> = {}
): ColDef {
  return {
    ...colWithErrorsColDef(field, {
      filter: 'agDateColumnFilter',
      cellDataType: 'dateString',
      valueFormatter: isoDateFormatter,
      valueGetter: nullifyRowValue(field),
      filterParams: {
        debounceMs: 500,
        comparator: isoDateOnlyComparator,
        includeTime: false
      }
    }),
    ...options
  };
}
export function dateTimeColWithErrorsColDef(
  field: keyof BillingReportRow,
  options: Partial<ColDef> = {}
): ColDef {
  return {
    ...colWithErrorsColDef(field, {
      filter: 'agDateColumnFilter',
      cellDataType: 'dateTimeString',
      valueFormatter: isoDateTimeFormatter,
      valueGetter: nullifyRowValue(field),
      filterParams: {
        debounceMs: 500,
        includeTime: true,
        comparator: isoDateTimeComparator
      }
    }),
    ...options
  };
}
